import { type Item, type ItemPlan, type ItemSuggestion, localDateOf } from '@hugo/core'
import { BellRingIcon, MessageSquareReplyIcon, SparklesIcon, TreePalmIcon } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { formatDue, shortAge, sourceLabel, suggestionLabel } from '@/lib/format'
import { useSettings } from '@/lib/items'
import { markerText } from '@/lib/lists'
import { awayHint, planBadgeLabel, reminderHint, repliedHint, showsPlanBadge } from '@/lib/plan'
import { showsPriority } from '@/lib/priority'
import { conditionLabel, ruleAt } from '@/lib/rules'
import { attention } from '@/lib/styles'
import { cn } from '@/lib/utils'
import { PriorityChip } from './priority-chip'
import { TagChips } from './tag-chips'

export type RowMode = 'inbox' | 'list' | 'snoozed' | 'done'

/**
 * Where hugo suggests an inbox item goes: "hugo: I owe · Fri 23 Oct", the reason on hover. Muted
 * unless hugo is sure (high confidence). A (accept) and D (dismiss) act on it.
 */
function SuggestionChip({
  suggestion,
  timeZone,
}: {
  suggestion: ItemSuggestion
  timeZone: string
}) {
  const sure = suggestion.confidence === 'high'
  return (
    <Badge
      variant="outline"
      data-testid="suggestion"
      data-confidence={suggestion.confidence}
      title={suggestion.reason || undefined}
      className={cn(
        'max-w-full',
        sure ? 'border-foreground/25 text-foreground' : 'font-normal text-muted-foreground',
      )}
    >
      <SparklesIcon aria-hidden="true" />
      <span className="truncate">{suggestionLabel(suggestion, timeZone)}</span>
    </Badge>
  )
}

/**
 * When hugo's plan gets a task or promise done that will be late: "Likely Fri 13 Nov", next to
 * the due date. Overdue items keep their overdue date and get this too.
 */
function PlanBadge({ plan, thisYear }: { plan: ItemPlan; thisYear: number }) {
  const label = planBadgeLabel(plan, thisYear)
  return (
    <Badge
      variant="outline"
      className={attention.badge}
      data-testid="plan-badge"
      data-status={plan.status}
      title="When it is likely done, at the hours a day you plan for tasks and promises"
    >
      {label}
    </Badge>
  )
}

/** Someone the user waits on should hear about time off: "You're away 26 Oct – 6 Nov". */
function AwayHint({ text }: { text: string }) {
  return (
    <Badge
      variant="outline"
      className={cn('max-w-full', attention.badge)}
      data-testid="away-hint"
      title="Let them know before you leave: see the heads-up in the details or the Outbox"
    >
      <TreePalmIcon aria-hidden="true" />
      <span className="truncate">{text}</span>
    </Badge>
  )
}

/** Someone the user waits on gets a reminder now: "Remind Marco". */
function ReminderHint({ text }: { text: string }) {
  return (
    <Badge
      variant="outline"
      className={cn('max-w-full', attention.badge)}
      data-testid="reminder-hint"
      title="The follow-up date has come and they have not written since: see the reminder in the details or the Outbox"
    >
      <BellRingIcon aria-hidden="true" />
      <span className="truncate">{text}</span>
    </Badge>
  )
}

/**
 * They wrote after the follow-up date was set, so hugo stops reminding: "Replied Tue 20 Oct", just
 * "Replied" on phones (the due date takes the rest of the row there).
 */
function RepliedHint({ text }: { text: string }) {
  return (
    <Badge
      variant="outline"
      className="max-w-full font-normal text-muted-foreground"
      data-testid="replied-hint"
      title={`${text}. No reminders while their answer is the newest: set a new follow-up date to start again.`}
    >
      <MessageSquareReplyIcon aria-hidden="true" />
      <span className="truncate sm:hidden">Replied</span>
      <span className="truncate max-sm:hidden">{text}</span>
    </Badge>
  )
}

/** Marks an item hugo filed by itself (rows in Tasks, I owe and Waiting on). */
function FiledByHugo() {
  return (
    <span
      role="img"
      aria-label="Filed by hugo"
      title="Filed by hugo. Press z right after to undo, or move it."
      className="inline-flex shrink-0 text-muted-foreground"
      data-testid="filed-by-hugo"
    >
      <SparklesIcon aria-hidden="true" className="size-3.5" />
    </span>
  )
}

/**
 * One item in a list. Click selects; clicking the selected row (or Enter) opens details.
 * The due date sits right of the title on wide screens and right of the second line on phones,
 * so the title always gets the full width it needs there.
 */
export function ItemRow({
  item,
  mode,
  selected,
  now,
  timeZone,
  onSelect,
  onOpen,
  onToggleDone,
  note,
}: {
  item: Item
  mode: RowMode
  selected: boolean
  now: Date
  timeZone: string
  onSelect: (item: Item) => void
  onOpen: (item: Item) => void
  onToggleDone?: (item: Item) => void
  /** One more fact, first on the meta line: e.g. "3 h 30 min today" on Today. */
  note?: string
}) {
  const overdue = item.dueAt !== null && item.status === 'open' && new Date(item.dueAt) < now
  const person = item.counterpart?.name
  const meta: string[] = []
  if (mode === 'inbox') {
    meta.push(sourceLabel(item.source))
    if (person) meta.push(`from ${person}`)
    meta.push(shortAge(item.requestedAt ?? item.createdAt, now))
  } else {
    // Who waits: "I owe Nina", "Waiting on Marco" (one list, decision 96); a task's person.
    const marker = markerText(item)
    if (marker) meta.push(marker)
  }
  if (mode === 'snoozed' && item.snoozedUntil) {
    meta.push(`until ${formatDue(item.snoozedUntil, false, timeZone)}`)
  }
  if (mode === 'done' && item.completedAt) {
    meta.push(item.closedReason === 'done' ? 'done' : (item.closedReason ?? 'closed'))
    meta.push(shortAge(item.completedAt, now))
  }
  // First: on phones the end of the line is cut behind the badges, and the note is what counts.
  if (note) meta.unshift(note)
  const showDue = item.dueAt !== null && mode !== 'done'
  const suggestion = mode === 'inbox' ? item.suggestion : null
  const filedByHugo = mode === 'list' && item.filedBy === 'agent'
  const planned = mode === 'list' || mode === 'snoozed'
  const plan = planned && showDue && showsPlanBadge(item) ? item.plan : null
  const awayFrom = planned && item.headsUp?.kind === 'waiting_away' ? item.headsUp.timeOff : null
  const remind = planned && item.headsUp?.kind === 'waiting_followup'
  const repliedAt = planned && item.kind === 'waiting' ? item.repliedAt : null
  const thisYear = plan || awayFrom || repliedAt ? localDateOf(now.getTime(), timeZone).year : 0
  const settings = useSettings().data
  const personalTags = settings?.personalTags
  const priorityRule = ruleAt(settings?.rules, item.priorityRuleIndex)
  const priority =
    mode !== 'done' && showsPriority(item.priority) ? (
      <PriorityChip
        priority={item.priority}
        rule={priorityRule ? conditionLabel(priorityRule.when) : null}
      />
    ) : null
  const tags =
    item.tags.length > 0 ? <TagChips tags={item.tags} personalTags={personalTags} /> : null
  const tagChips =
    priority || tags ? (
      <>
        {priority}
        {tags}
      </>
    ) : null
  const chip = suggestion ? (
    <SuggestionChip suggestion={suggestion} timeZone={timeZone} />
  ) : awayFrom ? (
    <AwayHint text={awayHint(awayFrom, thisYear)} />
  ) : remind ? (
    <ReminderHint text={reminderHint(person ?? null)} />
  ) : repliedAt ? (
    <RepliedHint text={repliedHint(repliedAt, timeZone, thisYear)} />
  ) : null
  const hasMeta = meta.length > 0 || chip !== null || tagChips !== null

  return (
    <div
      data-item-id={item.id}
      data-selected={selected}
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 transition-colors',
        selected ? 'bg-accent ring-2 ring-ring/40 ring-inset' : 'hover:bg-muted/50',
      )}
    >
      {mode === 'list' && onToggleDone ? (
        <Checkbox
          aria-label={`Mark "${item.title}" done`}
          checked={false}
          onCheckedChange={() => onToggleDone(item)}
          className="size-5"
        />
      ) : null}
      <button
        type="button"
        aria-current={selected || undefined}
        onClick={() => (selected ? onOpen(item) : onSelect(item))}
        onDoubleClick={() => onOpen(item)}
        className={cn(
          'grid min-w-0 flex-1 items-center gap-x-3 gap-y-0.5 text-left outline-none',
          showDue ? 'grid-cols-[minmax(0,1fr)_auto]' : 'grid-cols-1',
        )}
      >
        {filedByHugo ? (
          // The mark sits after the title (titles stay aligned) and never gets cut off.
          <span
            className={cn(
              'flex min-w-0 items-center gap-1.5',
              showDue && 'col-span-2 sm:col-span-1',
            )}
          >
            <span className="truncate text-sm font-medium">{item.title}</span>
            <FiledByHugo />
          </span>
        ) : (
          <span
            className={cn(
              'truncate text-sm font-medium',
              showDue && 'col-span-2 sm:col-span-1',
              mode === 'done' && 'text-muted-foreground line-through',
            )}
          >
            {item.title}
          </span>
        )}
        {chip || tagChips ? (
          // Phones: the chips wrap below the meta line instead of cutting it short.
          <span className="col-start-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            {meta.length > 0 ? (
              <span className="max-w-full truncate text-xs text-muted-foreground">
                {meta.join(' · ')}
              </span>
            ) : null}
            {chip}
            {tagChips}
          </span>
        ) : hasMeta ? (
          <span className="col-start-1 truncate text-xs text-muted-foreground">
            {meta.join(' · ')}
          </span>
        ) : null}
        {item.dueAt && showDue ? (
          // The due date stays at the right edge, where every row has it; "Likely …" goes left.
          // Phones: next to the meta line, or with two badges on a line of their own below it,
          // so who waits ("I owe Anna Keller") is never squeezed out.
          <span
            className={cn(
              'col-start-2 row-start-2 flex flex-wrap items-center justify-end gap-1 justify-self-end sm:row-start-1',
              hasMeta && 'sm:row-span-2',
              plan &&
                hasMeta &&
                'max-sm:col-span-2 max-sm:col-start-1 max-sm:row-start-3 max-sm:justify-self-start max-sm:justify-start',
            )}
          >
            {plan ? <PlanBadge plan={plan} thisYear={thisYear} /> : null}
            <Badge
              variant={overdue ? 'destructive' : 'outline'}
              data-testid="due-badge"
              title={overdue ? 'Overdue' : 'Due'}
            >
              {overdue ? 'Overdue · ' : ''}
              {formatDue(item.dueAt, item.dueAllDay, timeZone)}
            </Badge>
          </span>
        ) : null}
      </button>
    </div>
  )
}
